import { IconNames } from "@/components/ui/Icon";
import { doIt } from "@/utils/doIt";
import oMap from "@/utils/oMap";

//

interface EndpointItem {
  path: string;
  title: string;
  activeItemKey?: string;
  icon?: React.ReactElement | IconNames;
  sub?: EndpointObject;
}

export interface Endpoint {
  key: string;
  path: string;
  title: string;
  activeItemPath: string;
  icon: React.ReactElement | IconNames | undefined;
  sub: EndpointObject | undefined;
}

type EndpointObject = Record<string, EndpointItem>;

export type EndpointKeys<T extends EndpointObject> = T extends object
  ? {
      [K in keyof T & string]:
        | K
        | (T[K] extends { sub: EndpointObject } ? `${K}.${EndpointKeys<T[K]["sub"]>}` : never);
    }[keyof T & string]
  : never;

//

export class EndpointService<T extends EndpointObject, K = EndpointKeys<T>> {
  private array: Endpoint[] | null = null;

  //

  constructor(private endpoints: T) {}

  //

  public get data() {
    return this.endpoints;
  }

  //

  public toArray(tiny?: boolean): Endpoint[] {
    const array: Endpoint[] = [];

    const next = (links: EndpointObject, preKey = "", parentPath = "") => {
      oMap(links, ({ value, key }) => {
        const finishedKey = preKey ? `${preKey}.${key}` : key;
        const path = parentPath + value.path;
        if (tiny) {
          array.push({
            path: path,
            title: value.title,
            key: finishedKey,
            icon: value.icon,
          } as Endpoint);
        } else {
          array.push({
            path: path,
            title: value.title,
            key: finishedKey,
            icon: value.icon,
            sub: value.sub,
            activeItemPath: doIt(() => {
              const { activeItemKey } = value;
              if (activeItemKey === undefined) return path;
              return this.findItem(activeItemKey as K, true).path;
            }),
          });
        }

        if (value.sub) next(value.sub, finishedKey, path);
      });
    };

    next(this.endpoints);

    return array;
  }

  //

  public findItem(key: K, tiny?: boolean) {
    const array = this.getArray(tiny);
    const result = array.find((i) => i.key === key);
    if (!result) throw new Error("result is undefined");
    return result;
  }

  //

  public findPath(key: K, ...paths: string[]) {
    const item = this.findItem(key, true);
    const path = paths.join("");
    const slash = path.startsWith("?") ? "" : "/";
    return item.path + (path ? `${slash}${path}` : "");
  }

  //

  public findTitle(key: K) {
    const item = this.findItem(key, true);
    return item.title;
  }

  //

  public createPageTitle(key: K) {
    const title = this.findTitle(key);
    return `${title} — SELLPRO`;
  }

  //

  public findItemByPath(path: string) {
    const array = this.getArray();
    const item = array.find((i) => {
      const regex = i.path.replace(/\[.+\]/gi, ".+");
      return RegExp(`^${regex}$`).test(path);
    });
    return item ?? null;
  }

  //

  private getArray(tiny?: boolean) {
    return this.array ?? (this.array = this.toArray(tiny));
  }
}
